// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import type { IButtonStyles } from "@fluentui/react";
import type { IStackStyles, IStackTokens } from "@fluentui/react/lib/Stack";
import type {
  IRawStyle,
  IStyle,
  IStyleSet,
  ITheme,
} from "@fluentui/react/lib/Styling";
import {
  concatStyleSets,
  HighContrastSelector,
  mergeStyles,
  mergeStyleSets,
} from "@fluentui/react/lib/Styling";
import { getRTL, memoizeFunction } from "@fluentui/react/lib/Utilities";

import type { StandardControlSemanticColors } from "@1js/acui-common";
import {
  baseFabricFocusAfterSelector,
  baseFabricFocusSelector,
  commonHighContrastDisabledStyles,
  commonHighContrastFocusedDisabledStyles,
  commonHighContrastHoverFocusStyles,
  commonHighContrastOutlineFocusedDisabled,
  commonHighContrastOutlineHoverDisabled,
  commonHighContrastOutlineHoverFocus,
  commonHighContrastOutlineToggledOn,
  controlStylingConstants,
  ensureMinimumTargetSizesLegacy,
  getHighContrastAutoAdjustStyle,
  getInnerBorderStyles,
  getStandardControlSemanticColorsWithTheme,
} from "@1js/acui-common";
import {
  isThemingEnabled,
  customTokens,
  isNextStyleEnabled,
} from "@1js/acui-theme";

import { expandedGalleryChildrenGap } from "./galleryDefaultWidth";

export const defaultDimensions = {
  expandedGalleryButtonWidth: "64px",
  expandedGalleryButtonHeight: "64px",
  previewGalleryButtonWidth: "126px",
  previewGalleryButtonHeight: "34px",
  borderWidth: "4px",
  outlineSelectedWidth: "1px",
  previewGalleryButtonVerticalPadding: "4px",
  previewGalleryButtonHorizontalPadding: "4px",
  previewGalleryButtonVerticalPaddingWhenFocused: "2px",
  previewGalleryButtonHorizontalPaddingWhenFocused: "2px",
  previewGalleryButtonVerticalPaddingNext: "2px",
  previewGalleryButtonHorizontalPaddingNext: "2px",
  previewGalleryButtonVerticalPaddingWhenFocusedNext: "2px",
  previewGalleryButtonHorizontalPaddingWhenFocusedNext: "2px",
  previewGalleryButtonWidthNext: "126px",
  previewGalleryButtonHeightNext: "30px",
  previewGalleryContainerHeightNext: "32px",
};

export interface AppGalleryStyles {
  expandedGallery: IStyle;
  previewGallery: IStyle;
  galleryButtonInPreview: IButtonStyles;
  galleryButtonInExpanded: IButtonStyles;
  previewExpandButton: IButtonStyles;
  expandedGallerySectionTitle: IStyle;
  expandedGallerySectionTitleInnerSpan: IStyle;
  expandedGallerySectionDivider: IStyle;
  galleryContainer: IButtonStyles;
  previewStackStyle: IStackStyles;
  galleryStack: IStackStyles;
}

/**
 * Ensures that AppGalleryStyles are properly concatenated
 * Calling concatStyleSets directly doesn't correctly concatenate the IButtonStyles properties
 */
export function concatAppGalleryStyles(
  ...styles: (Partial<AppGalleryStyles> | undefined | null | false)[]
): AppGalleryStyles {
  // Helper to deal with casting between IButtonStyles and IStyleSet when calling call concatStyleSets
  function concatButtonStyles(
    styles: (IButtonStyles | undefined | null | false)[]
  ): IButtonStyles {
    const styleSets = styles as (IStyleSet | undefined | null | false)[];
    return concatStyleSets(...styleSets) as IButtonStyles;
  }
  function concatStackStyles(
    styles: (IStackStyles | undefined | null | false)[]
  ): IStackStyles {
    const styleSets = styles as (IStyleSet | undefined | null | false)[];
    return concatStyleSets(...styleSets) as IStackStyles;
  }

  return {
    expandedGallery: styles.map((s) => s && s.expandedGallery).filter((x) => x),
    previewGallery: styles.map((s) => s && s.previewGallery).filter((x) => x),
    expandedGallerySectionTitle: styles
      .map((s) => s && s.expandedGallerySectionTitle)
      .filter((x) => x),
    expandedGallerySectionTitleInnerSpan: styles
      .map((s) => s && s.expandedGallerySectionTitleInnerSpan)
      .filter((x) => x),
    expandedGallerySectionDivider: styles
      .map((s) => s && s.expandedGallerySectionDivider)
      .filter((x) => x),
    galleryButtonInPreview: concatButtonStyles(
      styles.map((s) => s && s.galleryButtonInPreview).filter((x) => x)
    ),
    galleryButtonInExpanded: concatButtonStyles(
      styles.map((s) => s && s.galleryButtonInExpanded).filter((x) => x)
    ),
    previewExpandButton: concatButtonStyles(
      styles.map((s) => s && s.previewExpandButton).filter((x) => x)
    ),
    galleryContainer: concatButtonStyles(
      styles.map((s) => s && s.galleryContainer).filter((x) => x)
    ),
    previewStackStyle: concatStackStyles(
      styles.map((s) => s && s.previewStackStyle).filter((x) => x)
    ),
    galleryStack: concatStackStyles(
      styles.map((s) => s && s.galleryStack).filter((x) => x)
    ),
  };
}

export interface AppGalleryClassNames {
  expandedGallery?: string;
  previewGallery?: string;
  galleryButtonInPreview?: string;
  galleryButtonInExpanded?: string;
  previewExpandButton?: string;
  galleryContainer: string;
}

function getPassiveGalleryContainerNextStyles(): IRawStyle {
  return {
    backgroundColor: customTokens.colorNeutralBackground1,
    borderColor: customTokens.colorNeutralStroke1,
    borderRadius: customTokens.borderRadiusMedium,
    height: defaultDimensions.previewGalleryContainerHeightNext,
    marginTop: 0,
  };
}

function getPassiveGalleryContainerStyles(theme: ITheme): IRawStyle {
  const standardControlSemanticColors: StandardControlSemanticColors =
    getStandardControlSemanticColorsWithTheme(theme);
  return {
    backgroundColor:
      standardControlSemanticColors.defaultGalleryBackgroundColor,
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: standardControlSemanticColors.inputBorderColor,
    boxSizing: "border-box",
    height: controlStylingConstants.controlHeight - 4,
    marginTop: 2,
  };
}

export const getGalleryClassNames = memoizeFunction(
  (
    theme: ITheme,
    customStyles: IButtonStyles,
    isExpanded: boolean,
    isFocused: boolean,
    disabled: boolean,
    buttonFocused: boolean,
    stack: boolean
  ): AppGalleryClassNames => {
    const standardControlSemanticColors: StandardControlSemanticColors =
      getStandardControlSemanticColorsWithTheme(theme);
    return mergeStyleSets({
      galleryContainer: [
        getPassiveGalleryContainerStyles(theme),
        isNextStyleEnabled() && getPassiveGalleryContainerNextStyles(),
        buttonFocused && {
          borderColor: standardControlSemanticColors.focusedBorderColor,
        },
        isExpanded &&
          isFocused && {
            borderColor: standardControlSemanticColors.inputBorderColor,
          },
        stack && {
          display: "flex",
        },
        customStyles.root,
        isExpanded && [customStyles.rootExpanded],
        isFocused && customStyles.rootFocused,
        disabled && {
          borderColor: "transparent",
          backgroundColor:
            standardControlSemanticColors.galleryExpandButtonDefaultBackgroundColor,
          selectors: {
            ":active": { borderColor: "transparent" },
          },
        },
      ],
    });
  }
);

const getCommonGalleryButtonStyles = memoizeFunction(
  (theme: ITheme): IButtonStyles => {
    const standardControlSemanticColors: StandardControlSemanticColors =
      getStandardControlSemanticColorsWithTheme(theme);
    return {
      root: {
        display: "inline-block",
        textDecoration: "none",
        textAlign: "center",
        cursor: "default",
        verticalAlign: "top",
        boxSizing: "border-box",
        backgroundColor:
          standardControlSemanticColors.defaultGalleryBackgroundColor,
        color: standardControlSemanticColors.galleryTextColor,
        padding: "10px 10px",
        border: "1px solid transparent",
        selectors: {
          ":focus": {
            borderColor: standardControlSemanticColors.galleryHoverFocusColor,
            selectors: {
              [HighContrastSelector]: [
                {
                  borderColor: "transparent",
                },
              ],
            },
          },
          [`${baseFabricFocusAfterSelector}, &:focus:after`]: {
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
          },
          [HighContrastSelector]: {
            borderColor: "transparent",
            ...getHighContrastAutoAdjustStyle(),
          },
        },
      },
      rootCheckedDisabled: [
        commonHighContrastFocusedDisabledStyles,
        {
          selectors: {
            ":focus": commonHighContrastOutlineFocusedDisabled,
          },
        },
      ],
      rootHovered: {
        color: standardControlSemanticColors.galleryTextColor,
      },
      rootPressed: {
        color: standardControlSemanticColors.galleryTextColor,
        borderColor:
          standardControlSemanticColors.galleryPressedBackgroundColor,
        selectors: {
          [HighContrastSelector]: [
            {
              borderColor: "transparent",
            },
          ],
        },
      },
      rootChecked: [
        {
          borderColor: standardControlSemanticColors.gallerySelectedColor,
        },
        commonHighContrastHoverFocusStyles,
        commonHighContrastOutlineToggledOn,
      ],
      rootFocused: [
        commonHighContrastHoverFocusStyles,
        commonHighContrastOutlineHoverFocus,
      ],
      rootDisabled: [
        {
          opacity: 0.4,
          pointerEvents: "none",
          selectors: {
            ":focus": commonHighContrastOutlineFocusedDisabled,
          },
        },
        commonHighContrastDisabledStyles,
      ],
      flexContainer: {
        display: "flex",
        height: "100%",
        flexWrap: "nowrap",
        justifyContent: "center",
        alignItems: "center",
        lineHeight: "normal",
      },
    };
  }
);

export const getGalleryButtonInExpandedStyles = memoizeFunction(
  (
    theme: ITheme,
    focusOnHover: boolean,
    wideBorderForSelectedButtonEnabled?: boolean
  ): IButtonStyles => {
    const standardControlSemanticColors: StandardControlSemanticColors =
      getStandardControlSemanticColorsWithTheme(theme);
    return {
      root: {
        height: defaultDimensions.expandedGalleryButtonHeight,
        width: defaultDimensions.expandedGalleryButtonWidth,
        selectors: {
          ":focus": {
            borderWidth: defaultDimensions.borderWidth,
          },
          [`${baseFabricFocusAfterSelector}, &:focus:after`]: {
            outline: 0,
          },
        },
      },
      rootFocused: {
        selectors: {
          [`${baseFabricFocusSelector}, &:focus`]: {
            borderWidth: defaultDimensions.borderWidth,
            [HighContrastSelector]: [
              {
                borderColor: "highlight",
                backgroundColor: "highlight",
              },
            ],
          },
        },
      },
      rootPressed: [commonHighContrastHoverFocusStyles],
      rootHovered: [
        !focusOnHover && {
          borderWidth: defaultDimensions.borderWidth,
          borderColor: standardControlSemanticColors.galleryHoverFocusColor,
        },
        commonHighContrastHoverFocusStyles,
        commonHighContrastOutlineHoverFocus,
      ],
      rootCheckedHovered: [
        commonHighContrastHoverFocusStyles,
        commonHighContrastOutlineHoverFocus,
      ],
      rootChecked: {
        borderWidth: wideBorderForSelectedButtonEnabled
          ? defaultDimensions.borderWidth
          : defaultDimensions.outlineSelectedWidth,
        borderColor: standardControlSemanticColors.gallerySelectedColor,
        selectors: {
          [`${baseFabricFocusAfterSelector}, &:focus:after`]: [
            getInnerBorderStyles({
              width: 2,
              color: standardControlSemanticColors.gallerySelectedColor,
              gap: 0,
            }),
            {
              borderRadius: 0,
              [HighContrastSelector]: {
                borderColor: "transparent",
              },
            },
          ],
          [`${baseFabricFocusSelector}, &:focus`]: [
            {
              selectors: {
                [HighContrastSelector]: [
                  {
                    borderColor: "highlight",
                  },
                ],
              },
            },
            commonHighContrastHoverFocusStyles,
            commonHighContrastOutlineHoverFocus,
          ],
        },
      },
    };
  }
);

const getFocusStyleNext = memoizeFunction((borderRadius: string): IRawStyle => {
  return {
    selectors: {
      ":after": [
        getInnerBorderStyles({
          width: 2,
          color: customTokens.colorStrokeFocus2,
          gap: -1,
          radius: borderRadius,
        }),
      ],
    },
  };
});

export const getGalleryButtonInPreviewNextStyles = memoizeFunction(
  // Note Gallery Buttons in preview aren't focused on hover so use root hovered for styling hover
  (): IButtonStyles => {
    const focusBorderPaddingStyles = getFocusStyleNext(
      customTokens.borderRadiusMedium
    );
    const rootCommonDisabledStyles: IRawStyle = {
      paddingTop: defaultDimensions.previewGalleryButtonVerticalPaddingNext,
      paddingBottom: defaultDimensions.previewGalleryButtonVerticalPaddingNext,
      paddingLeft: defaultDimensions.previewGalleryButtonHorizontalPaddingNext,
      paddingRight: defaultDimensions.previewGalleryButtonHorizontalPaddingNext,
      selectors: {
        [`${baseFabricFocusSelector}, &:focus`]: [focusBorderPaddingStyles],
      },
    };
    return {
      root: [
        {
          height: defaultDimensions.previewGalleryButtonHeightNext,
          width: defaultDimensions.previewGalleryButtonWidthNext,
          border: "none",
          paddingTop: defaultDimensions.previewGalleryButtonVerticalPaddingNext,
          paddingBottom:
            defaultDimensions.previewGalleryButtonVerticalPaddingNext,
          paddingLeft:
            defaultDimensions.previewGalleryButtonHorizontalPaddingNext,
          paddingRight:
            defaultDimensions.previewGalleryButtonHorizontalPaddingNext,
          backgroundColor: "transparent",
          borderRadius: customTokens.borderRadiusMedium,
        },
      ],
      rootHovered: [
        {
          backgroundColor: customTokens.colorNeutralBackground1Hover,
          selectors: {
            [`${baseFabricFocusSelector}, &:focus`]: [focusBorderPaddingStyles],
          },
        },
      ],
      rootFocused: [
        {
          backgroundColor: "transparent",
        },
        focusBorderPaddingStyles,
      ],
      rootPressed: [
        {
          backgroundColor: customTokens.colorNeutralBackground1Pressed,
        },
      ],
      rootDisabled: [
        rootCommonDisabledStyles,
        {
          color: customTokens.colorNeutralForegroundDisabled,
          backgroundColor: "transparent",
          selectors: {
            ":focus": {
              [HighContrastSelector]: {
                backgroundColor: "transparent",
                outline: "highlight solid 2px",
              },
            },
          },
        },
      ],
      rootChecked: {
        backgroundColor: customTokens.colorNeutralBackground1Selected,
        outline: `${defaultDimensions.outlineSelectedWidth} solid ${customTokens.colorNeutralStrokeAccessible}`,
        outlineOffset: `-${defaultDimensions.outlineSelectedWidth}`,
        selectors: {
          [`${baseFabricFocusSelector}, &:focus`]: [focusBorderPaddingStyles],
        },
      },
      rootCheckedHovered: [
        {
          backgroundColor: customTokens.colorNeutralBackground1Hover,
          outline: `${defaultDimensions.outlineSelectedWidth} solid ${customTokens.colorNeutralStrokeAccessibleHover}`,
          outlineOffset: `-${defaultDimensions.outlineSelectedWidth}`,
          selectors: {
            [`${baseFabricFocusSelector}, &:focus`]: [focusBorderPaddingStyles],
            [HighContrastSelector]: {
              commonHighContrastOutlineHoverFocus,
            },
          },
        },
      ],
      rootCheckedPressed: [
        {
          backgroundColor: customTokens.colorNeutralBackground1Pressed,
          outline: `${defaultDimensions.outlineSelectedWidth} solid ${customTokens.colorNeutralStrokeAccessiblePressed}`,
          outlineOffset: `-${defaultDimensions.outlineSelectedWidth}`,
        },
        commonHighContrastHoverFocusStyles,
      ],
      rootCheckedDisabled: [
        rootCommonDisabledStyles,
        {
          backgroundColor: customTokens.colorNeutralBackground1Selected,
          selectors: {
            [`${baseFabricFocusSelector}, &:focus`]: [
              {
                backgroundColor: "transparent",
              },
              focusBorderPaddingStyles,
            ],
            [HighContrastSelector]: {
              color: "highlightText",
              "svg > path.OfficeIconColors_HighContrast": {
                fill: "highlightText !important",
              },
            },
          },
        },
      ],
    };
  }
);

export const getGalleryButtonInPreviewStyles = memoizeFunction(
  // Note Gallery Buttons in preview aren't focused on hover so use root hovered for styling hover
  (theme: ITheme): IButtonStyles => {
    const standardControlSemanticColors: StandardControlSemanticColors =
      getStandardControlSemanticColorsWithTheme(theme);
    const focusBorderPaddingStyles = isNextStyleEnabled()
      ? {}
      : {
          border:
            "2px solid " + standardControlSemanticColors.focusedBorderColor,
          paddingTop:
            defaultDimensions.previewGalleryButtonVerticalPaddingWhenFocused,
          paddingBottom:
            defaultDimensions.previewGalleryButtonVerticalPaddingWhenFocused,
          paddingLeft:
            defaultDimensions.previewGalleryButtonHorizontalPaddingWhenFocused,
          paddingRight:
            defaultDimensions.previewGalleryButtonHorizontalPaddingWhenFocused,
        };
    const rootCommonDisabledStyles: IRawStyle = {
      pointerEvents: "none",
      cursor: "default",
      border: "none",
      paddingTop: defaultDimensions.previewGalleryButtonVerticalPadding,
      paddingBottom: defaultDimensions.previewGalleryButtonVerticalPadding,
      paddingLeft: defaultDimensions.previewGalleryButtonHorizontalPadding,
      paddingRight: defaultDimensions.previewGalleryButtonHorizontalPadding,
      selectors: {
        ":hover": [
          {
            outline: 0,
            backgroundColor: "transparent",
          },
          commonHighContrastOutlineHoverDisabled,
        ],
        ":focus": [
          commonHighContrastOutlineFocusedDisabled,
          {
            backgroundColor:
              standardControlSemanticColors.galleryHoverFocusColor,
          },
          focusBorderPaddingStyles,
        ],
        ":active": {
          outline: 0,
          backgroundColor: "transparent",
        },
      },
    };
    return {
      root: {
        height: defaultDimensions.previewGalleryButtonHeight,
        width: defaultDimensions.previewGalleryButtonWidth,
        border: "none",
        paddingTop: defaultDimensions.previewGalleryButtonVerticalPadding,
        paddingBottom: defaultDimensions.previewGalleryButtonVerticalPadding,
        paddingLeft: defaultDimensions.previewGalleryButtonHorizontalPadding,
        paddingRight: defaultDimensions.previewGalleryButtonHorizontalPadding,
        backgroundColor: "transparent",
      },
      rootHovered: [
        {
          backgroundColor: standardControlSemanticColors.galleryHoverFocusColor,
          borderWidth: 0,
          selectors: {
            ":focus": [focusBorderPaddingStyles],
          },
        },
        commonHighContrastHoverFocusStyles,
        commonHighContrastOutlineHoverFocus,
      ],
      rootFocused: [
        commonHighContrastHoverFocusStyles,
        {
          backgroundColor: standardControlSemanticColors.galleryHoverFocusColor,
        },
        focusBorderPaddingStyles,
      ],
      rootPressed: [
        {
          backgroundColor:
            standardControlSemanticColors.galleryPressedBackgroundColor,
        },
        commonHighContrastHoverFocusStyles,
      ],
      rootDisabled: [
        rootCommonDisabledStyles,
        {
          color: standardControlSemanticColors.textDisabledColor,
          backgroundColor: "transparent",
        },
        commonHighContrastDisabledStyles,
      ],
      rootChecked: {
        backgroundColor: standardControlSemanticColors.gallerySelectedColor,
        outline: `${defaultDimensions.outlineSelectedWidth} solid ${customTokens.colorNeutralStrokeAccessible}`,
        outlineOffset: `-${defaultDimensions.outlineSelectedWidth}`,
        selectors: {
          ":focus": [
            focusBorderPaddingStyles,
            commonHighContrastHoverFocusStyles,
          ],
        },
      },
      rootCheckedHovered: [
        {
          backgroundColor: standardControlSemanticColors.galleryHoverFocusColor,
          outline: `${defaultDimensions.outlineSelectedWidth} solid ${customTokens.colorNeutralStrokeAccessibleHover}`,
          outlineOffset: `-${defaultDimensions.outlineSelectedWidth}`,
          selectors: {
            ":focus": [focusBorderPaddingStyles],
            [HighContrastSelector]: {
              commonHighContrastOutlineHoverFocus,
            },
          },
        },
        commonHighContrastHoverFocusStyles,
      ],
      rootCheckedPressed: [
        {
          backgroundColor:
            standardControlSemanticColors.galleryPressedBackgroundColor,
          outline: `${defaultDimensions.outlineSelectedWidth} solid ${customTokens.colorNeutralStrokeAccessiblePressed}`,
          outlineOffset: `-${defaultDimensions.outlineSelectedWidth}`,
        },
        commonHighContrastHoverFocusStyles,
      ],
      rootCheckedDisabled: [
        rootCommonDisabledStyles,
        {
          backgroundColor: standardControlSemanticColors.gallerySelectedColor,
          selectors: {
            ":focus": [
              commonHighContrastOutlineFocusedDisabled,
              {
                backgroundColor:
                  standardControlSemanticColors.galleryHoverFocusColor,
                left: 0,
                top: 0,
                marginRight: 0,
              },
              focusBorderPaddingStyles,
            ],
          },
        },
      ],
    };
  }
);

export const galleryButtonInPreviewTransitionDuration = 150;
export const galleryButtonInPreviewTransitionClassName = "gallery-item-animate";

const getGalleryButtonInPreviewTransitionStyles = memoizeFunction(
  (): IButtonStyles => {
    const widthTransitionSettings = `${galleryButtonInPreviewTransitionDuration}ms cubic-bezier(0.1, 0.9, 0.2, 1)`;
    const enterExitTransition =
      `opacity ${galleryButtonInPreviewTransitionDuration}ms linear,` +
      `width ${widthTransitionSettings}, ` +
      `min-width ${widthTransitionSettings}, ` +
      `padding-left ${widthTransitionSettings}, ` +
      `padding-right ${widthTransitionSettings}`;
    return {
      root: {
        selectors: {
          ["&.gallery-item-animate-enter"]: {},
          ["&.gallery-item-animate-enter:not(.gallery-item-animate-enter-active)"]:
            {
              opacity: 0,
              width: 0,
              minWidth: 0,
              paddingLeft: 0,
              paddingRight: 0,
            },
          ["&.gallery-item-animate-enter-active"]: {
            transition: enterExitTransition,
          },
          ["&.gallery-item-animate-exit"]: {},
          ["&.gallery-item-animate-exit-active"]: {
            transition: enterExitTransition,
            width: 0,
            minWidth: 0,
            opacity: 0,
            paddingLeft: 0,
            paddingRight: 0,
          },
        },
      },
    };
  }
);

export const getPreviewExpandButtonNextStyles = memoizeFunction(
  (): IButtonStyles => {
    const borderRadius = `${customTokens.borderRadiusNone} ${customTokens.borderRadiusMedium} ${customTokens.borderRadiusMedium} ${customTokens.borderRadiusNone}`;
    return {
      root: {
        height: defaultDimensions.previewGalleryButtonHeightNext,
        backgroundColor: customTokens.colorNeutralBackground1,
        color: customTokens.colorNeutralForeground1,
        border: "1px solid " + customTokens.colorNeutralStroke1,
        borderTopRightRadius: customTokens.borderRadiusMedium,
        borderBottomRightRadius: customTokens.borderRadiusMedium,
        [`${baseFabricFocusSelector}, &:focus`]: [
          getFocusStyleNext(borderRadius),
        ],
      },
      rootHovered: {
        backgroundColor: customTokens.colorNeutralBackground1Hover,
        borderColor: customTokens.colorNeutralStroke1Hover,
        [`${baseFabricFocusSelector}, &:focus`]: [
          getFocusStyleNext(borderRadius),
        ],
      },
      rootPressed: {
        backgroundColor: customTokens.colorNeutralBackground1Pressed,
        borderColor: customTokens.colorNeutralStroke1Pressed,
        color: customTokens.colorNeutralForeground1,
      },
      rootFocused: {
        backgroundColor: customTokens.colorNeutralBackground1,
      },
      rootDisabled: {
        backgroundColor: customTokens.colorNeutralBackground1,
        borderColor: customTokens.colorNeutralStrokeDisabled,
        [`${baseFabricFocusSelector}, &:focus`]: [
          getFocusStyleNext(borderRadius),
        ],
      },
      rootExpanded: {
        backgroundColor: customTokens.colorNeutralBackground1Selected,
        borderColor: customTokens.colorNeutralStroke1Selected,
        [`${baseFabricFocusSelector}, &:focus`]: [
          getFocusStyleNext(borderRadius),
        ],
      },
      rootExpandedHovered: {
        backgroundColor: customTokens.colorNeutralBackground1Selected,
        borderColor: customTokens.colorNeutralStroke1Selected,
        [`${baseFabricFocusSelector}, &:focus`]: [
          getFocusStyleNext(borderRadius),
        ],
      },
      icon: {
        fill: customTokens.colorNeutralForeground1,
        width: 12,
        height: 12,
        lineHeight: 12,
      },
    };
  }
);

export const getPreviewExpandButtonStyles = memoizeFunction(
  (theme: ITheme): IButtonStyles => {
    const standardControlSemanticColors: StandardControlSemanticColors =
      getStandardControlSemanticColorsWithTheme(theme);
    return {
      root: {
        height: defaultDimensions.previewGalleryButtonHeight,
        width: "16px",
        minWidth: "16px",
        backgroundColor:
          standardControlSemanticColors.galleryExpandButtonDefaultBackgroundColor,
        color: standardControlSemanticColors.galleryTextColor,
        border: "1px solid " + standardControlSemanticColors.inputBorderColor,
        borderRight: "none",
        borderRadius: "unset",
        padding: 0,
      },

      rootHovered: {
        backgroundColor: standardControlSemanticColors.galleryHoverFocusColor,
      },

      rootPressed: {
        backgroundColor:
          standardControlSemanticColors.galleryPressedBackgroundColor,
        borderColor:
          standardControlSemanticColors.galleryPressedBackgroundColor,
        color: standardControlSemanticColors.galleryTextColor,
      },

      rootFocused: {
        borderColor: standardControlSemanticColors.focusedBorderColor,
      },

      rootDisabled: {
        pointerEvents: "none",
        opacity: 0.4,
        backgroundColor:
          standardControlSemanticColors.galleryExpandButtonDefaultBackgroundColor,
        borderColor: "transparent",
        selectors: {
          ":hover": [
            {
              outline: 0,
              backgroundColor: "transparent",
            },
            commonHighContrastOutlineHoverDisabled,
          ],
          ":focus": commonHighContrastOutlineFocusedDisabled,
          ":active": {
            outline: 0,
            backgroundColor: "transparent",
          },
        },
      },

      rootExpanded: {
        backgroundColor: standardControlSemanticColors.galleryHoverFocusColor,
      },

      rootExpandedHovered: {
        backgroundColor: standardControlSemanticColors.galleryHoverFocusColor,
      },

      icon: {
        fill: isThemingEnabled()
          ? standardControlSemanticColors.textColor
          : "#444444",
        width: 12,
        height: 12,
        lineHeight: 12,
      },
    };
  }
);

export const getCommonPreviewStackStyles = memoizeFunction(
  (): IStackStyles => ({
    root: {
      overflow: "hidden",
    },
  })
);

const getStackStyles = memoizeFunction(
  (): IStackStyles => ({
    root: {
      padding: expandedGalleryChildrenGap,
      width: "max-content",
    },
    inner: {
      margin: "1px 0px",
      padding: 0,
    },
  })
);

export const getStackTokens = memoizeFunction(
  (
    maxWidth?: number,
    customExpandedGalleryChildrenGap?: number
  ): IStackTokens => {
    return {
      childrenGap:
        customExpandedGalleryChildrenGap ?? expandedGalleryChildrenGap,
      maxWidth,
      padding: "2px 0px",
    };
  }
);

export const getAppGalleryStyles = memoizeFunction(
  (
    theme: ITheme,
    customStyles?: Partial<AppGalleryStyles>,
    focusOnHover?: boolean,
    minimumTargetSizesEnabled: boolean = false,
    wideBorderForSelectedButtonEnabled?: boolean
  ): AppGalleryStyles => {
    const standardControlSemanticColors: StandardControlSemanticColors =
      getStandardControlSemanticColorsWithTheme(theme);
    return {
      ...customStyles,
      galleryContainer: concatStyleSets(
        customStyles && customStyles.galleryContainer
      ),
      expandedGallery: customStyles && customStyles.expandedGallery,
      previewGallery: mergeStyles(
        {
          display: "inline-block",
          marginLeft: "4px",
          marginRight: "4px",
        },
        customStyles && customStyles.previewGallery
      ),
      galleryButtonInExpanded: concatStyleSets(
        getCommonGalleryButtonStyles(theme),
        getGalleryButtonInExpandedStyles(
          theme,
          focusOnHover ?? true,
          wideBorderForSelectedButtonEnabled
        ),
        customStyles && customStyles.galleryButtonInExpanded
      ),
      previewExpandButton: ensureMinimumTargetSizesLegacy(
        concatStyleSets(
          getPreviewExpandButtonStyles(theme),
          isNextStyleEnabled() && getPreviewExpandButtonNextStyles(),
          customStyles && customStyles.previewExpandButton
        ),
        minimumTargetSizesEnabled
      ),
      galleryButtonInPreview: concatStyleSets(
        getCommonGalleryButtonStyles(theme),
        getGalleryButtonInPreviewStyles(theme),
        getGalleryButtonInPreviewTransitionStyles(),
        isNextStyleEnabled() && getGalleryButtonInPreviewNextStyles(),
        customStyles && customStyles.galleryButtonInPreview
      ),
      expandedGallerySectionTitle: mergeStyles(
        {
          fontSize: 14,
          fontWeight: 600,
          paddingRight: 14,
          paddingLeft: 14,
          backgroundColor: standardControlSemanticColors.menuBackgroundColor,
          color: standardControlSemanticColors.menuItemTextColor,
          position: "relative",
          boxSizing: "border-box",
          height: 32,
          lineHeight: 32,
          cursor: "default",
        },
        customStyles && customStyles.expandedGallerySectionTitle
      ),
      expandedGallerySectionTitleInnerSpan: mergeStyles(
        {
          verticalAlign: "top",
        },
        customStyles && customStyles.expandedGallerySectionTitleInnerSpan
      ),
      expandedGallerySectionDivider: mergeStyles(
        {
          display: "block",
          height: "1px",
          backgroundColor: "#DADADA",
          position: "relative",
        },
        customStyles && customStyles.expandedGallerySectionDivider
      ),
      previewStackStyle: concatStyleSets(
        getCommonPreviewStackStyles(),
        customStyles && customStyles.previewStackStyle
      ),
      galleryStack: mergeStyleSets(
        getStackStyles(),
        customStyles && customStyles.galleryStack
      ),
    };
  }
);

export const getMergedGalleryAndButtonStyles = memoizeFunction(
  (
    stylesFromGallery?: Partial<AppGalleryStyles>,
    stylesFromGalleryButton?: IButtonStyles
  ): Partial<AppGalleryStyles> => {
    return {
      ...stylesFromGallery,
      galleryButtonInExpanded: concatStyleSets(
        stylesFromGallery && stylesFromGallery.galleryButtonInExpanded,
        stylesFromGalleryButton
      ),
      galleryButtonInPreview: concatStyleSets(
        stylesFromGallery && stylesFromGallery.galleryButtonInPreview,
        stylesFromGalleryButton
      ),
    };
  }
);

export const getMergedAppGalleryStylesMLR = memoizeFunction(
  (customStyles?: Partial<AppGalleryStyles>): Partial<AppGalleryStyles> => {
    return concatAppGalleryStyles(appGalleryStylesMLR, customStyles);
  }
);

export const appGalleryStylesMLR: Partial<AppGalleryStyles> = {
  expandedGallerySectionTitle: {
    height: 28,
    lineHeight: 28,
    fontSize: 12,
  },
};

export const getHiddenFlyoutStyles = memoizeFunction(
  (customStyles?: IButtonStyles) => {
    return mergeStyleSets(customStyles, {
      root: {
        visibility: "hidden",
        position: "absolute",
      },
    });
  }
);

export const getAppGallerySplitButtonStyles = memoizeFunction(
  (customStyles?: IButtonStyles): IButtonStyles => {
    return mergeStyleSets(customStyles, {
      label: {
        width: "inherit",
        direction: getRTL() ? "rtl" : "ltr",
        textAlign: "start",
      },
    });
  }
);

export const getGallerySplitButtonStyle = memoizeFunction(
  (theme: ITheme, labelStyle?: IButtonStyles): IButtonStyles => {
    const semanticColors = getStandardControlSemanticColorsWithTheme(theme);
    return {
      root: {
        height: 36,
        width: "100%",
        padding: 0,
        border: "0px transparent solid",
        backgroundColor: "inherit",
        borderRadius: 0,
        selectors: {
          ":hover": {
            backgroundColor: semanticColors.hoverBackgroundColor,
            border: "0px transparent none",
          },
          ":focus-visible": {
            outline: "none",
          },
        },
        fontWeight: 400,
      },
      label: mergeStyles({ fontWeight: 400 }, labelStyle?.label),
      icon: {
        height: 16,
        width: 16,
      },
      splitButtonContainer: {
        marginRight: -1,
        marginLeft: 1,
        height: 36,
        justifyContent: "space-between",
        border: "0px transparent solid",
        backgroundColor: "transparent",
        width: "100%",
        selectors: {
          ":hover": {
            backgroundColor: semanticColors.hoverBackgroundColor,
            border: "0px transparent none",
          },
          ":focus-visible": {
            outline: "none",
          },
        },
      },
      splitButtonContainerChecked: [
        {
          backgroundColor: semanticColors.toggledBackgroundColor,
          outline: "1px solid",
          outlineColor: semanticColors.selectedBorderColor,
          borderRadius: 0,
        },
        commonHighContrastHoverFocusStyles,
      ],
      splitButtonMenuButton: {
        border: "none",
        height: 36,
        width: 28,
        backgroundColor: "inherit",
        selectors: {
          ":hover": {
            backgroundColor: semanticColors.hoverBackgroundColor,
            border: "none",
          },
          ":focus-visible": {
            outline: "none",
          },
        },
      },
      splitButtonMenuButtonExpanded: {
        backgroundColor: "transparent",
      },
      splitButtonDivider: {
        backgroundColor: semanticColors.borderColor,
        right: 27,
        position: "absolute",
        alignSelf: "center",
        height: 16,
        width: 1,
      },
    };
  }
);
